import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { Editor } from 'primeng/editor';
import { EditorSpellcheckDirective } from '../../shared/directives/editor-spellcheck.directive';
import { MessageService } from 'primeng/api';
import { Toast } from "primeng/toast";
import { ProjectService } from '../../services/project.service';
import { ProjectModel } from '../../models/project.model';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import { EffectiveSkidConfigurationService } from '../../services/effective-skid-configuration.service';
import { getProjectSkidConfigurationParts, SkidConfigurationParts } from '../../utils/skid-configuration.utils';
import { buildProjectDownloadFileName } from '../../utils/download-file-name.utils';

@Component({
  selector: 'app-skidspec',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    Editor,
    Toast,
    EditorSpellcheckDirective
  ],
  templateUrl: './skidspec.html',
  styleUrl: './skidspec.css'
})
export class SkidspecComponent implements OnInit {
  projectInfo: ProjectModel = {} as ProjectModel;
  effectiveSkidParts: SkidConfigurationParts = [0, 0, 0, 0, 0, 0, 0];
  saving: boolean = false;
  canSave: boolean = false;

  // Inverter model mapping
  inverterModels: { [key: number]: string } = {
    1: 'PVU-L0840URN',
    2: 'PVU-L1050URN',
    3: 'PVU-L1400URN',
    4: 'PVU-L1680URN',
    5: 'Other'
  };

  constructor(
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private projectService: ProjectService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private effectiveSkidConfiguration: EffectiveSkidConfigurationService
  ) { }

  ngOnInit(): void {
    console.log('SkidSpec dialog data:', this.config.data);

    if (this.config.data && this.config.data.projectInfo) {
      this.projectInfo = { ...this.config.data.projectInfo };
      this.effectiveSkidParts = getProjectSkidConfigurationParts(this.projectInfo);
    }

    // Check if user can save (Admin or PM)
    this.canSave = this.config.data?.canSave || false;

    if (this.projectInfo.projectId) {
      this.effectiveSkidConfiguration.clear(this.projectInfo.projectId);
    }

    this.effectiveSkidConfiguration.getEffectiveParts(this.projectInfo).subscribe(parts => {
      this.effectiveSkidParts = parts;
      this.cdr.markForCheck();
    });
  }

  getTotalSkids(): number {
    return this.effectiveSkidParts.reduce((sum, value) => sum + value, 0);
  }

  getInverterModelName(): string {
    if (!this.projectInfo.inverterModelId) {
      return '';
    }
    return this.inverterModels[this.projectInfo.inverterModelId] || '';
  }

  saveSkidSpec(): void {
    this.saving = true;
    this.cdr.markForCheck();

    // Only send the managerNotes field for update
    const updateData = {
      projectId: this.projectInfo.projectId,
      managerNotes: this.projectInfo.managerNotes
    };

    this.projectService.updateSkidSpec(updateData).subscribe({
      next: (res) => {
        this.saving = false;
        this.cdr.markForCheck();

        if (res != null && res == true) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Data saved successfully.'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error occurred while saving data.'
          });
        }
      },
      error: (error) => {
        this.saving = false;
        this.cdr.markForCheck();

        console.error('Error saving skid spec:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error occurred while saving data.'
        });
      }
    });
  }

  exportSkidSpec(): void {
    if (!this.projectInfo.projectId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Project ID is required for export.'
      });
      return;
    }

    this.projectService.downloadSkidSpec(this.projectInfo.projectId).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.Response) {
          const fileName = buildProjectDownloadFileName(this.projectInfo, 'SkidSpecification', 'xlsx');
          let data = event as HttpResponse<Blob>;
          const downloadedFile = new Blob([data.body as BlobPart], {
            type: data.body?.type
          });
          if (downloadedFile.type != "") {
            const a = document.createElement('a');
            a.setAttribute('style', 'display:none;');
            document.body.appendChild(a);
            a.download = fileName;
            a.href = URL.createObjectURL(downloadedFile);
            a.target = '_blank';
            a.click();
            document.body.removeChild(a);
          }
        }
      },
      error: (error: any) => {
        console.error('Error exporting skid spec:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to export MV Power Station specification.'
        });
      }
    });
  }

  onClose(): void {
    this.ref.close();
  }
}
